// "Last week → now": the first block of the Meeting view (owner ask
// 2026-10-12: "here's what we set out to do last week, here's the current
// state of all projects as we currently see them"). Three parts, all from
// GET /admin/roadmap/overview (two stored reports diffed, nothing new stored):
//
//  - Done since: what finished between the two reports, promised or not.
//  - What we set out to do: only what the earlier report said was DUE by the
//    latest one (sub-actions and initiatives with a target on or before that
//    day), each with its outcome. Owner, 2026-10-12: listing every open
//    sub-action "makes it look like we didn't do anything" when nobody set
//    out to finish 53 things in a week; the undated ones are a footnote.
//  - What moved: initiatives whose status, target or next step changed.
//  - Where every project stands now: one line per active initiative, the
//    earlier report's state beside the latest, with a change chip; the
//    tiles narrow it.
//
// The page's Team / Person / Find filters narrow all three (visibleKeys);
// the counts are derived from the narrowed rows (weekOverviewUtils.ts).
//
// Owner, 2026-10-14 ("sub tabs or something instead of infinite scroll"):
// the Meeting section is now three views, so `view` picks which blocks this
// panel draws — 'week' (done since, set out to do, what moved) on the Last
// week → now tab, 'projects' (tiles + the then → now table) on the Where
// projects stand tab; 'all' is the old single scroll.

import { Fragment, useMemo, useState } from 'react';
import type { InitiativeState, OverviewDelta, OverviewRow, OverviewSnapshot, RoadmapOverview } from '../../services/roadmapReports';
import { resolveActionKey, STATUS_LABELS, teamsLine } from './roadmapViewUtils';
import type { ModalSection } from './TimelineChart';
import { fmtDay, parseDay } from './roadmapTimelineUtils';
import { HealthChip } from './HealthChip';
import {
  DELTA_LABELS,
  doneSince,
  movedParts,
  movedRows,
  OUTCOME_LABELS,
  rowHasDelta,
  SET_OUT_FOLD,
  SINCE_OPTIONS,
  sliceOverview,
  tally,
  TILE_DELTAS,
  TILE_LABELS,
} from './weekOverviewUtils';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

const SHOWN_HEALTH: ReadonlySet<string> = new Set(['late', 'at_risk', 'stuck']);

interface Props {
  data: RoadmapOverview;
  /** Initiative keys the page's filters keep; null = everything. */
  visibleKeys: ReadonlySet<string> | null;
  /** True when a filter is on, so an empty block says "under this filter". */
  filtered: boolean;
  /** The live rows, for the health chip beside this week's status. */
  initiatives: readonly InitiativeState[];
  /** Open the initiative pop-up; with an action key, the pop-up scoped to that sub-action. */
  onSelect: (key: string, section?: ModalSection, action?: string) => void;
  sinceDays: number;
  onSinceDays: (days: number) => void;
  /** Which blocks to draw (see the file comment); 'all' = every block. */
  view?: 'all' | 'week' | 'projects';
}

const day = (iso: string | null): string | null => {
  const d = parseDay(iso);
  return d ? fmtDay(d) : null;
};

/** A sub-action named on an overview line: a button to its pop-up when the
 * live initiative still carries it (by title), else plain text. */
function ActionLink({ initiative, title, onOpen }: { initiative: InitiativeState | null | undefined; title: string; onOpen: (actionKey: string) => void }) {
  const key = resolveActionKey(initiative, title);
  if (!key) return <>{title}</>;
  return (
    <button
      type="button"
      className={styles.actionLink}
      onClick={() => onOpen(key)}
      title="Open this sub-action"
      data-action="roadmap_action_open"
      data-target-initiative-key={initiative?.initiative_key}
      data-target-action-key={key}
    >
      {title}
    </button>
  );
}

function StatusCell({ s, health, onOpenAction }: { s: OverviewSnapshot | null; health?: InitiativeState | null; onOpenAction?: (actionKey: string) => void }) {
  if (!s) return <span className={styles.meta}>not on the roadmap</span>;
  return (
    <>
      <span className={`${tableStyles.statusChip} ${tableStyles[`status_${s.status}`] ?? ''}`}>{STATUS_LABELS[s.status]}</span>
      {health && SHOWN_HEALTH.has(health.health) && <> <HealthChip health={health.health} initiative={health} /></>}
      <div className={styles.meta}>
        {s.target_date ? `target ${day(s.target_date)}` : 'no date'}
        {s.next_action ? <> · next: {onOpenAction ? <ActionLink initiative={health} title={s.next_action} onOpen={onOpenAction} /> : s.next_action}</> : ''}
        {s.external_blocker ? ` · waiting on ${s.external_blocker}` : ''}
      </div>
    </>
  );
}

export function WeekOverviewPanel({ data, visibleKeys, filtered, initiatives, onSelect, sinceDays, onSinceDays, view = 'all' }: Props) {
  const showWeek = view !== 'projects';
  const showProjects = view !== 'week';
  const [tile, setTile] = useState<OverviewDelta | null>(null);
  const [allSetOut, setAllSetOut] = useState(false);
  const slice = useMemo(() => sliceOverview(data, visibleKeys), [data, visibleKeys]);
  const t = useMemo(() => tally(slice.rows, slice.setOut), [slice]);
  const { changed, fresh } = useMemo(() => movedRows(slice.rows), [slice.rows]);
  const done = useMemo(() => doneSince(slice.rows), [slice.rows]);
  const byKey = useMemo(() => new Map(initiatives.map((i) => [i.initiative_key, i])), [initiatives]);
  const active = useMemo(
    () => slice.rows.filter((r) => r.now.status !== 'done' && r.now.status !== 'dropped' && !r.parked).filter((r) => !tile || rowHasDelta(r, tile)),
    [slice.rows, tile],
  );
  const thenDay = day(data.then_report?.report_date ?? null);
  const nowDay = day(data.now_report.report_date);
  const setOutShown = allSetOut ? slice.setOut : slice.setOut.slice(0, SET_OUT_FOLD);
  const title = (r: OverviewRow) => (
    <button type="button" className={styles.linkBtn} onClick={() => onSelect(r.initiative_key)} data-action="overview_open_initiative" data-target-initiative-key={r.initiative_key}>
      {r.title}
    </button>
  );

  return (
    <section className={`${styles.panel} ${styles.overview}`} aria-label={view === 'projects' ? 'Where projects stand' : 'Last week → now'} data-testid="week-overview" data-view={view}>
      <div className={styles.panelHead}>
        <div>
          <h2 className={styles.overviewTitle}>{view === 'projects' ? 'Where projects stand' : 'Last week → now'}</h2>
          <div className={styles.meta} data-testid="overview-sub">
            {thenDay ? `Since the ${thenDay} report, as of ${nowDay}` : `As of ${nowDay}; no earlier report to compare with yet`}
            {` · ${t.active} active initiative${t.active === 1 ? '' : 's'}`}
            {filtered ? ' under the current filters' : ''}
          </div>
        </div>
        <div className={styles.segment} role="group" aria-label="Compare with">
          {SINCE_OPTIONS.map((o) => (
            <button
              key={o.days}
              type="button"
              className={o.days === sinceDays ? styles.segmentOn : undefined}
              onClick={() => onSinceDays(o.days)}
              aria-pressed={o.days === sinceDays}
              data-action="overview_since"
              data-record-value={String(o.days)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {showProjects && data.then_report && (
        <div className={styles.overviewTiles} data-testid="overview-tiles">
          {TILE_DELTAS.map((d) => (
            <button
              key={d}
              type="button"
              className={`${styles.overviewTile} ${tile === d ? styles.overviewTileOn : ''}`}
              onClick={() => setTile((cur) => (cur === d ? null : d))}
              aria-pressed={tile === d}
              data-action="overview_tile"
              data-record-value={d}
            >
              <span className={styles.overviewTileN}>{t.byDelta[d]}</span>
              <span className={styles.overviewTileL}>{TILE_LABELS[d]}</span>
            </button>
          ))}
        </div>
      )}

      {showWeek && data.then_report && (
        <div className={styles.overviewBlock} data-testid="overview-done">
          <h3>
            Done since {thenDay}{' '}
            <span className={styles.meetingCount}>
              {done.length === 0 ? 'nothing marked done' : `${done.filter((d) => d.title === null).length} initiative${done.filter((d) => d.title === null).length === 1 ? '' : 's'} · ${done.filter((d) => d.title !== null).length} sub-action${done.filter((d) => d.title !== null).length === 1 ? '' : 's'}`}
            </span>
          </h3>
          {done.length === 0 ? (
            <p className={styles.meetingEmpty}>Nothing was marked done between the two reports{filtered ? ' under the current filters' : ''}.</p>
          ) : (
            <ul className={styles.overviewLines}>
              {done.map((d) => (
                <li key={`${d.initiative_key}:${d.title ?? ''}`}>
                  <span><span className={`${styles.chip} ${styles.chip_done}`}>{d.title === null ? 'Initiative done' : 'Done'}</span></span>
                  <span>
                    <span className={styles.overviewLineTitle}>
                      {d.title === null ? d.initiative_title : <ActionLink initiative={byKey.get(d.initiative_key)} title={d.title} onOpen={(k) => onSelect(d.initiative_key, 'dates', k)} />}
                    </span>
                    <span className={styles.meta}>
                      {d.title !== null && (
                        <button type="button" className={styles.linkBtn} onClick={() => onSelect(d.initiative_key)} data-action="overview_open_initiative" data-target-initiative-key={d.initiative_key}>
                          {d.initiative_title}
                        </button>
                      )}
                      {d.title === null && (
                        <button type="button" className={styles.linkBtn} onClick={() => onSelect(d.initiative_key)} data-action="overview_open_initiative" data-target-initiative-key={d.initiative_key}>
                          open
                        </button>
                      )}
                      {d.owner ? ` · ${d.owner}` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showWeek && data.then_report && (
        <div className={styles.overviewBlock} data-testid="overview-setout">
          <h3>
            What we set out to do by {nowDay}{' '}
            <span className={styles.meetingCount}>
              {t.setOut.total === 0
                ? 'nothing was dated'
                : `${t.setOut.total} due · ${t.setOut.done} done · ${t.setOut.stillOpen} not done yet${t.setOut.moved ? ` · ${t.setOut.moved} moved` : ''}${t.setOut.dropped ? ` · ${t.setOut.dropped} dropped` : ''}${t.setOut.gone ? ` · ${t.setOut.gone} gone` : ''}`}
            </span>
          </h3>
          <p className={styles.meta}>What the {thenDay} report said was due by {nowDay}: sub-actions and initiatives with a stated date. Done first.</p>
          {slice.setOut.length === 0 ? (
            <p className={styles.meetingEmpty} data-testid="overview-setout-empty">
              Nothing in the {thenDay} report had a date on or before {nowDay}{filtered ? ' under the current filters' : ''}.
              {!filtered && data.counts.undated_open > 0 && ` ${data.counts.undated_open} open sub-action${data.counts.undated_open === 1 ? ' carried' : 's carried'} no date; ask Merlin for one when it matters.`}
            </p>
          ) : (
            <ul className={styles.overviewLines}>
              {setOutShown.map((s) => (
                <li key={`${s.kind}:${s.initiative_key}:${s.key}`}>
                  <span><span className={`${styles.chip} ${styles[`chip_${s.outcome}`] ?? ''}`}>{s.outcome === 'moved' && s.target_now ? `Moved to ${day(s.target_now)}` : OUTCOME_LABELS[s.outcome]}</span></span>
                  <span>
                    <span className={styles.overviewLineTitle}>
                      {s.kind === 'initiative' ? (
                        <button type="button" className={styles.linkBtn} onClick={() => onSelect(s.initiative_key)} data-action="overview_open_initiative" data-target-initiative-key={s.initiative_key}>
                          {s.title}
                        </button>
                      ) : (
                        <button type="button" className={styles.actionLink} onClick={() => onSelect(s.initiative_key, 'dates', s.key)} title="Open this sub-action" data-action="roadmap_action_open" data-target-initiative-key={s.initiative_key} data-target-action-key={s.key}>
                          {s.title}
                        </button>
                      )}
                    </span>
                    <span className={styles.meta}>
                      {s.kind === 'sub_action' ? (
                        <>
                          <button type="button" className={styles.linkBtn} onClick={() => onSelect(s.initiative_key)} data-action="overview_open_initiative" data-target-initiative-key={s.initiative_key}>
                            {s.initiative_title}
                          </button>
                          {' · '}
                        </>
                      ) : 'the initiative itself · '}
                      {s.people.length > 0 ? `${s.people.join(', ')} · ` : ''}
                      due {day(s.target_then)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
          {slice.setOut.length > SET_OUT_FOLD && (
            <button type="button" className={styles.meetingMore} onClick={() => setAllSetOut((v) => !v)} data-action="overview_setout_toggle">
              {allSetOut ? 'Show fewer' : `Show all ${slice.setOut.length}`}
            </button>
          )}
          {slice.setOut.length > 0 && !filtered && data.counts.undated_open > 0 && (
            <p className={styles.meta} data-testid="overview-undated-note">
              Not counted: {data.counts.undated_open} open sub-action{data.counts.undated_open === 1 ? '' : 's'} with no date in the {thenDay} report.
            </p>
          )}
        </div>
      )}

      {showWeek && data.then_report && (
        <div className={styles.overviewBlock} data-testid="overview-moved">
          <h3>
            What moved <span className={styles.meetingCount}>{changed.length} changed · {fresh.length} new</span>
          </h3>
          {changed.length + fresh.length === 0 ? (
            <p className={styles.meetingEmpty}>Nothing moved between the two reports{filtered ? ' under the current filters' : ''}.</p>
          ) : (
            <ul className={styles.overviewLines}>
              {changed.map((r) => (
                <li key={r.initiative_key}>
                  <span>{r.deltas.filter((d) => d !== 'unchanged').map((d) => <span key={d} className={`${styles.chip} ${styles[`chip_${d}`] ?? ''}`}>{DELTA_LABELS[d]}</span>)}</span>
                  <span>
                    <span className={styles.overviewLineTitle}>{title(r)}</span>
                    <span className={styles.meta}>
                      {movedParts(r, day).map((p, n) => (
                        <Fragment key={n}>
                          {n > 0 ? ' · ' : ''}
                          {p.action ? <>next step now: <ActionLink initiative={byKey.get(r.initiative_key)} title={p.action} onOpen={(k) => onSelect(r.initiative_key, 'dates', k)} /></> : p.text}
                        </Fragment>
                      ))}
                      {r.owner ? ` · ${r.owner}` : ''}
                    </span>
                  </span>
                </li>
              ))}
              {fresh.map((r) => (
                <li key={r.initiative_key}>
                  <span><span className={`${styles.chip} ${styles.chip_new}`}>New</span></span>
                  <span>
                    <span className={styles.overviewLineTitle}>{title(r)}</span>
                    <span className={styles.meta}>{STATUS_LABELS[r.now.status]}{r.now.target_date ? ` · target ${day(r.now.target_date)}` : ' · no date yet'}{r.owner ? ` · ${r.owner}` : ''}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showProjects && (
      <div className={styles.overviewBlock} data-testid="overview-table">
        <h3>
          Where every project stands now <span className={styles.meetingCount}>{active.length}{tile ? ` of ${t.active}` : ''} active</span>
        </h3>
        {tile && (
          <p className={styles.meta}>
            Showing “{TILE_LABELS[tile]}”.{' '}
            <button type="button" className={styles.linkBtn} onClick={() => setTile(null)} data-action="overview_tile_clear">Show all</button>
          </p>
        )}
        <table className={styles.overviewTable}>
          <thead>
            <tr>
              <th>Initiative</th>
              <th>{thenDay ?? 'Earlier'}</th>
              <th aria-hidden="true" />
              <th>{nowDay}</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {active.length === 0 ? (
              <tr><td colSpan={5} className={styles.meetingEmpty}>Nothing to show{filtered ? ' under the current filters' : ''}.</td></tr>
            ) : (
              active.map((r) => (
                <tr key={r.initiative_key} className={r.deltas.includes('unchanged') ? styles.overviewDim : undefined} data-testid={`overview-row-${r.initiative_key}`}>
                  <td>
                    <span className={styles.overviewLineTitle}>{title(r)}</span>
                    <div className={styles.meta}>
                      {teamsLine(byKey.get(r.initiative_key) ?? r)}{r.owner ? ` · ${r.owner}` : ''}{r.open_questions ? ` · ${r.open_questions} open question${r.open_questions === 1 ? '' : 's'}` : ''}
                    </div>
                  </td>
                  <td><StatusCell s={r.then} /></td>
                  <td className={styles.overviewArrow} aria-hidden="true">→</td>
                  <td><StatusCell s={r.now} health={byKey.get(r.initiative_key) ?? null} onOpenAction={(k) => onSelect(r.initiative_key, 'dates', k)} /></td>
                  <td>{r.deltas.map((d) => <span key={d} className={`${styles.chip} ${styles[`chip_${d}`] ?? ''}`}>{DELTA_LABELS[d]}</span>)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}
      {showWeek && !data.then_report && (
        <p className={styles.meetingEmpty} data-testid="overview-no-earlier">No earlier report to compare with yet; the week view fills in after the next roadmap run.</p>
      )}
    </section>
  );
}
